import type { SortingState } from "@tanstack/react-table";

import { EXPIRING_CRITICAL_DAYS, EXPIRING_SOON_DAYS } from "@domainstack/constants";
import type { ProviderCategory, TrackedDomainWithDetails } from "@domainstack/types";
import { calculateDaysRemaining } from "@domainstack/utils/expiry";

// ---------------------------------------------------------------------------
// Provider Types
// ---------------------------------------------------------------------------

interface AvailableProvider {
  id: string;
  name: string;
  domain: string | null;
}

export type AvailableProvidersByCategory = Record<ProviderCategory, AvailableProvider[]>;

/** Filter values for domain verification status */
const STATUS_FILTERS = ["verified", "pending"] as const;
export type StatusFilter = (typeof STATUS_FILTERS)[number];

/** Filter values for domain health status */
export const HEALTH_FILTERS = ["healthy", "expiring", "expired"] as const;
export type HealthFilter = (typeof HEALTH_FILTERS)[number];

/** Valid filter values for runtime validation of URL params */
const VALID_STATUS_FILTERS = new Set<StatusFilter>(STATUS_FILTERS);

/** Valid health filter values for runtime validation of URL params */
const VALID_HEALTH_FILTERS = new Set<HealthFilter>(HEALTH_FILTERS);

/** Severity shown on a domain's health badge. */
export type HealthSeverity = "healthy" | "warning" | "critical" | "unknown";

/**
 * Whole days until a tracked domain expires, or null when there is nothing to
 * count (unverified, missing date, unparseable date).
 *
 * Every dashboard reading of "days left" goes through here so the filter, the
 * summary counts, the row badge, and the sort all classify a domain the same
 * way. `calculateDaysRemaining` is the same helper the expiry notifications
 * use, so the dashboard and the emails agree on the day count too.
 */
function getDaysUntilExpiry(
  expirationDate: Date | null,
  verified: boolean,
  now: Date,
): number | null {
  if (!verified || !expirationDate || Number.isNaN(expirationDate.getTime())) return null;

  const days = calculateDaysRemaining(expirationDate, now);
  return Number.isNaN(days) ? null : days;
}

/**
 * Determine health status based on expiration date
 */
function getHealthStatus(
  expirationDate: Date | null,
  verified: boolean,
  now: Date,
): HealthFilter | null {
  const daysUntilExpiry = getDaysUntilExpiry(expirationDate, verified, now);
  if (daysUntilExpiry === null) return null;

  if (daysUntilExpiry <= 0) return "expired";
  if (daysUntilExpiry <= EXPIRING_SOON_DAYS) return "expiring";
  return "healthy";
}

/**
 * Determine badge severity based on expiration date.
 *
 * Shares its day count and thresholds with the health filter above, so a row
 * badge can never contradict the "Expiring Soon" summary beside it.
 */
export function getHealthSeverity(
  expirationDate: Date | null,
  verified: boolean,
  now: Date,
): HealthSeverity {
  const daysUntilExpiry = getDaysUntilExpiry(expirationDate, verified, now);
  if (daysUntilExpiry === null) return "unknown";

  if (daysUntilExpiry <= EXPIRING_CRITICAL_DAYS) return "critical";
  if (daysUntilExpiry <= EXPIRING_SOON_DAYS) return "warning";
  return "healthy";
}

export const DASHBOARD_VIEW_MODE_OPTIONS = ["grid", "table"] as const;
export type DashboardViewModeOptions = (typeof DASHBOARD_VIEW_MODE_OPTIONS)[number];

export const DASHBOARD_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
export type DashboardPageSizeOptions = (typeof DASHBOARD_PAGE_SIZE_OPTIONS)[number];

/** True when the current page is past the last page of `itemCount` rows. */
export function isPagePastEnd(itemCount: number, pageIndex: number, pageSize: number): boolean {
  const pageCount = Math.max(1, Math.ceil(itemCount / pageSize));
  return pageIndex >= pageCount;
}

export interface DashboardPreferencesDefault {
  viewMode: DashboardViewModeOptions;
  pageSize: DashboardPageSizeOptions;
  columnVisibility: Record<string, boolean>;
}

export const DASHBOARD_PREFERENCES_DEFAULT: DashboardPreferencesDefault = {
  viewMode: "grid",
  pageSize: 10,
  columnVisibility: {}, // Empty means all columns visible (default)
};

/**
 * Sort option using table column format: "columnId.direction"
 * This keeps grid and table sorting perfectly aligned
 */
export type SortOption = `${string}.${"asc" | "desc"}`;

export interface SortOptionConfig {
  value: SortOption;
  label: string;
  shortLabel: string;
  direction: "asc" | "desc";
}

export const SORT_OPTIONS: SortOptionConfig[] = [
  {
    value: "domainName.asc",
    label: "Name (A-Z)",
    shortLabel: "Name",
    direction: "asc",
  },
  {
    value: "domainName.desc",
    label: "Name (Z-A)",
    shortLabel: "Name",
    direction: "desc",
  },
  {
    value: "expirationDate.asc",
    label: "Expiry (Soonest first)",
    shortLabel: "Expiry",
    direction: "asc",
  },
  {
    value: "expirationDate.desc",
    label: "Expiry (Furthest first)",
    shortLabel: "Expiry",
    direction: "desc",
  },
  {
    value: "createdAt.desc",
    label: "Recently added",
    shortLabel: "Added",
    direction: "desc",
  },
];

export const DEFAULT_SORT: SortOption = "domainName.asc";

/**
 * Columns where unverified domains should NOT be pushed to the end.
 * For all other columns, unverified/pending domains will appear last.
 */
const COLUMNS_WITHOUT_VERIFICATION_SORT = new Set(["domainName", "verified", "createdAt"]);

/**
 * Sort domains based on sort option (using table column format).
 * For columns other than domainName, verified, and createdAt,
 * unverified/pending domains are always pushed to the end of the list.
 */
export function sortDomains(
  domains: TrackedDomainWithDetails[],
  sortOption: SortOption,
): TrackedDomainWithDetails[] {
  const sorted = [...domains];
  const [columnId, direction] = sortOption.split(".") as [string, "asc" | "desc"];
  const isDesc = direction === "desc";
  const pushUnverifiedToEnd = !COLUMNS_WITHOUT_VERIFICATION_SORT.has(columnId);

  switch (columnId) {
    case "domainName":
      sorted.sort((a, b) =>
        isDesc
          ? b.domainName.localeCompare(a.domainName)
          : a.domainName.localeCompare(b.domainName),
      );
      break;
    case "expirationDate":
      sorted.sort((a, b) => {
        // Push unverified domains to the end
        if (pushUnverifiedToEnd) {
          if (!a.verified && b.verified) return 1;
          if (a.verified && !b.verified) return -1;
        }
        // Put domains without expiry date at the end (among their verification group)
        if (!a.expirationDate && !b.expirationDate) return 0;
        if (!a.expirationDate) return 1;
        if (!b.expirationDate) return -1;
        return isDesc
          ? b.expirationDate.getTime() - a.expirationDate.getTime()
          : a.expirationDate.getTime() - b.expirationDate.getTime();
      });
      break;
    case "createdAt":
      sorted.sort((a, b) =>
        isDesc
          ? b.createdAt.getTime() - a.createdAt.getTime()
          : a.createdAt.getTime() - b.createdAt.getTime(),
      );
      break;
  }

  return sorted;
}

/**
 * Parse sort string to TanStack Table SortingState
 * Format: "columnId.asc" or "columnId.desc"
 */
export function parseSortParam(sortParam: string): SortingState {
  const parts = sortParam.split(".");
  if (parts.length === 2) {
    const [columnId, direction] = parts;
    if (direction === "asc" || direction === "desc") {
      return [{ id: columnId, desc: direction === "desc" }];
    }
  }

  // Default fallback
  return [{ id: "domainName", desc: false }];
}

/**
 * Convert TanStack Table SortingState to sort string
 * Format: "columnId.asc" or "columnId.desc"
 */
export function serializeSortState(sorting: SortingState): string {
  if (sorting.length === 0) return DEFAULT_SORT;

  const [first] = sorting;
  return `${first.id}.${first.desc ? "desc" : "asc"}`;
}

// ---------------------------------------------------------------------------
// Domain Data Extraction
// ---------------------------------------------------------------------------

/**
 * Extract unique TLDs from domains for filter dropdown
 */
export function extractAvailableTlds(domains: TrackedDomainWithDetails[]): string[] {
  const tldSet = new Set<string>();
  for (const domain of domains) {
    if (domain.tld) tldSet.add(domain.tld);
  }
  return Array.from(tldSet).sort();
}

/**
 * Extract unique providers from domains, grouped by category
 */
export function extractAvailableProviders(
  domains: TrackedDomainWithDetails[],
): AvailableProvidersByCategory {
  const providersByCategory: AvailableProvidersByCategory = {
    registrar: [],
    dns: [],
    hosting: [],
    email: [],
    ca: [],
  };

  const registrarMap = new Map<string, AvailableProvider>();
  const dnsMap = new Map<string, AvailableProvider>();
  const hostingMap = new Map<string, AvailableProvider>();
  const emailMap = new Map<string, AvailableProvider>();
  const caMap = new Map<string, AvailableProvider>();

  for (const domain of domains) {
    if (!domain.verified || domain.archivedAt !== null) continue;

    const { registrar, dns, hosting, email, ca } = domain;

    if (registrar.id && registrar.name) {
      if (!registrarMap.has(registrar.id)) {
        registrarMap.set(registrar.id, {
          id: registrar.id,
          name: registrar.name,
          domain: registrar.domain,
        });
      }
    }
    if (dns.id && dns.name) {
      if (!dnsMap.has(dns.id)) {
        dnsMap.set(dns.id, {
          id: dns.id,
          name: dns.name,
          domain: dns.domain,
        });
      }
    }
    if (hosting.id && hosting.name) {
      if (!hostingMap.has(hosting.id)) {
        hostingMap.set(hosting.id, {
          id: hosting.id,
          name: hosting.name,
          domain: hosting.domain,
        });
      }
    }
    if (email.id && email.name) {
      if (!emailMap.has(email.id)) {
        emailMap.set(email.id, {
          id: email.id,
          name: email.name,
          domain: email.domain,
        });
      }
    }
    if (ca.id && ca.name) {
      if (!caMap.has(ca.id)) {
        caMap.set(ca.id, {
          id: ca.id,
          name: ca.name,
          domain: ca.domain,
        });
      }
    }
  }

  providersByCategory.registrar = Array.from(registrarMap.values()).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
  providersByCategory.dns = Array.from(dnsMap.values()).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
  providersByCategory.hosting = Array.from(hostingMap.values()).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
  providersByCategory.email = Array.from(emailMap.values()).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
  providersByCategory.ca = Array.from(caMap.values()).sort((a, b) => a.name.localeCompare(b.name));

  return providersByCategory;
}

/**
 * Create a flat set of all valid provider IDs for validation
 */
export function getValidProviderIds(availableProviders: AvailableProvidersByCategory): Set<string> {
  const ids = new Set<string>();
  for (const category of Object.values(availableProviders)) {
    for (const provider of category) {
      ids.add(provider.id);
    }
  }
  return ids;
}

/**
 * Compute health stats for the dashboard badges.
 * `expiringSoon` matches the Expiring Soon filter (not expired).
 */
export function computeHealthStats(domains: TrackedDomainWithDetails[], now: Date) {
  let expiringSoon = 0;
  let pendingVerification = 0;

  for (const domain of domains) {
    if (!domain.verified) {
      pendingVerification++;
      continue;
    }
    if (getHealthStatus(domain.expirationDate, domain.verified, now) === "expiring") {
      expiringSoon++;
    }
  }

  return { expiringSoon, pendingVerification };
}

// ---------------------------------------------------------------------------
// Filter Validation & Filtering
// ---------------------------------------------------------------------------

/**
 * Validate and filter status values from URL params
 */
export function validateStatusFilters(values: string[]): StatusFilter[] {
  return values.filter((s): s is StatusFilter => VALID_STATUS_FILTERS.has(s as StatusFilter));
}

/**
 * Validate and filter health values from URL params
 */
export function validateHealthFilters(values: string[]): HealthFilter[] {
  return values.filter((h): h is HealthFilter => VALID_HEALTH_FILTERS.has(h as HealthFilter));
}

/**
 * Filter criteria for domain filtering
 */
export interface DomainFilterCriteria {
  search: string;
  domainId: string | null;
  status: StatusFilter[];
  health: HealthFilter[];
  tlds: string[];
  providers: string[];
}

/**
 * Filter domains based on search, status, health, TLDs, and providers
 */
export function filterDomains(
  domains: TrackedDomainWithDetails[],
  criteria: DomainFilterCriteria,
  validProviderIds: Set<string>,
  now: Date,
): TrackedDomainWithDetails[] {
  const statusSet = new Set(criteria.status);
  const healthSet = new Set(criteria.health);
  const tldSet = new Set(criteria.tlds);
  // Trimmed so pasted values and trailing spaces still match.
  const searchLower = criteria.search.trim().toLowerCase();

  return domains.filter((domain) => {
    // Filter by specific domain ID
    if (criteria.domainId && domain.id !== criteria.domainId) return false;

    // Filter by search term
    if (searchLower && !domain.domainName.toLowerCase().includes(searchLower)) {
      return false;
    }

    // Filter by verification status
    if (statusSet.size > 0) {
      const domainStatus = domain.verified ? "verified" : "pending";
      if (!statusSet.has(domainStatus)) return false;
    }

    // Filter by health status
    if (healthSet.size > 0) {
      const healthStatus = getHealthStatus(domain.expirationDate, domain.verified, now);
      if (!healthStatus || !healthSet.has(healthStatus)) return false;
    }

    // Filter by TLD
    if (tldSet.size > 0 && !tldSet.has(domain.tld)) {
      return false;
    }

    // Filter by provider
    if (criteria.providers.length > 0) {
      if (!domain.verified) return false;
      const validSelectedProviders = criteria.providers.filter((id) => validProviderIds.has(id));
      if (validSelectedProviders.length === 0) return true;
      const providerSet = new Set(validSelectedProviders);
      const hasMatch =
        (domain.registrar.id && providerSet.has(domain.registrar.id)) ||
        (domain.dns.id && providerSet.has(domain.dns.id)) ||
        (domain.hosting.id && providerSet.has(domain.hosting.id)) ||
        (domain.email.id && providerSet.has(domain.email.id)) ||
        (domain.ca.id && providerSet.has(domain.ca.id));
      if (!hasMatch) return false;
    }

    return true;
  });
}

// ---------------------------------------------------------------------------
// Confirmation Dialog
// ---------------------------------------------------------------------------

export type ConfirmAction =
  | { type: "remove"; domainId: string; domainName: string }
  | { type: "archive"; domainId: string; domainName: string }
  | { type: "bulk-archive"; domainIds: string[]; count: number }
  | { type: "bulk-delete"; domainIds: string[]; count: number };

export function getConfirmDialogContent(action: ConfirmAction) {
  switch (action.type) {
    case "remove":
      return {
        title: "Remove domain?",
        description: `Are you sure you want to stop tracking ${action.domainName}?`,
        confirmLabel: "Remove",
        variant: "destructive" as const,
      };
    case "archive":
      return {
        title: "Archive domain?",
        description: `Are you sure you want to archive ${action.domainName}? You can reactivate it later from the Archived section.`,
        confirmLabel: "Archive",
        variant: "default" as const,
      };
    case "bulk-archive":
      return {
        title: `Archive ${action.count} domain${action.count === 1 ? "" : "s"}?`,
        description: `Are you sure you want to archive ${action.count} domain${action.count === 1 ? "" : "s"}? You can reactivate them later from the Archived section.`,
        confirmLabel: "Archive All",
        variant: "default" as const,
      };
    case "bulk-delete":
      return {
        title: `Delete ${action.count} domain${action.count === 1 ? "" : "s"}?`,
        description: `Are you sure you want to stop tracking ${action.count} domain${action.count === 1 ? "" : "s"}?`,
        confirmLabel: "Delete All",
        variant: "destructive" as const,
      };
  }
}
